import { useId } from 'react';
import { Box } from '@mui/material';

/**
 * Kompanjon-/maskotkomponent för NPF-barnvyn.
 *
 * Rendrerar en av fem karaktärer som ren inline-SVG (inga externa bild-assets,
 * PWA/CSP-säkert). Karaktären uttrycker fyra humör-tillstånd ('mood') via
 * ansikte/pose plus en subtil CSS @keyframes-animation. All animation stängs
 * av om användaren har 'prefers-reduced-motion: reduce'.
 *
 * Detta är en ren leaf-/presentationskomponent: ingen state, ingen context,
 * ingen localStorage. CharacterContext och onboarding byggs ovanpå denna.
 */

export type CharacterName = 'fox' | 'robot' | 'bear' | 'star' | 'frog';

export type CharacterMood = 'idle' | 'happy' | 'thinking' | 'celebrate';

export type CharacterDefinition = {
  /** Intern nyckel, t.ex. 'fox'. Matchar CharacterName. */
  name: CharacterName;
  /** Standardnamn enligt designförslaget, t.ex. 'Ronja'. */
  defaultName: string;
  /** Svensk etikett på djuret/figuren, t.ex. 'Räven'. */
  label: string;
  /** Kort beskrivning av personlighet (för onboarding-väljaren). */
  personality: string;
};

/**
 * Karaktärslistan i designförslagets ordning. Exporteras för att
 * onboarding-väljaren ska kunna rendera valbara karaktärer utan att
 * duplicera metadata.
 */
export const characters: ReadonlyArray<CharacterDefinition> = [
  { name: 'fox', defaultName: 'Ronja', label: 'Räven', personality: 'Nyfiken, energisk' },
  { name: 'robot', defaultName: 'Pixel', label: 'Roboten', personality: 'Lugn, tålmodig' },
  { name: 'bear', defaultName: 'Teddy', label: 'Björnen', personality: 'Varm, trygg' },
  { name: 'star', defaultName: 'Stella', label: 'Stjärnan', personality: 'Glad, peppar' },
  { name: 'frog', defaultName: 'Kalle', label: 'Grodan', personality: 'Tokig, rolig' },
];

const moodLabels: Record<CharacterMood, string> = {
  idle: 'lugn',
  happy: 'glad',
  thinking: 'funderar',
  celebrate: 'firar',
};

export type CharacterProps = {
  /** Vilken karaktär som ska visas. */
  character: CharacterName;
  /** Humör-tillstånd som styr ansikte/pose + animation. Default 'idle'. */
  mood?: CharacterMood;
  /**
   * Storlek i px (kvadratisk via viewBox). Kan även vara CSS-värde,
   * t.ex. '100%' för att fylla föräldern. Default 96.
   */
  size?: number | string;
  /**
   * Om true behandlas figuren som rent dekorativ (aria-hidden) och får ingen
   * etikett. Sätt detta när en synlig pratbubbla redan beskriver kontexten.
   * Default false → figuren får en beskrivande aria-label (karaktär + humör).
   */
  decorative?: boolean;
};

// Mjuk skugga under figuren – ger förankring utan hård kontrast.
const groundShadow = <ellipse cx="60" cy="112" rx="26" ry="5" fill="rgba(45,49,66,0.10)" />;

/**
 * Ansiktsdrag delas mellan karaktärer där det går. Returnerar ögon + mun
 * som SVG-element baserat på mood. cx/cy = ansiktets ungefärliga centrum.
 */
const renderFace = (mood: CharacterMood) => {
  // Ögon: 'happy'/'celebrate' = glada bågar, 'thinking' = halvslutna,
  // 'idle' = runda öppna.
  const eyes =
    mood === 'happy' || mood === 'celebrate' ? (
      <>
        <path
          d="M46 54 q5 -6 10 0"
          fill="none"
          stroke="#2D3142"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M64 54 q5 -6 10 0"
          fill="none"
          stroke="#2D3142"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </>
    ) : mood === 'thinking' ? (
      <>
        <circle cx="51" cy="55" r="3.2" fill="#2D3142" />
        <circle cx="69" cy="55" r="3.2" fill="#2D3142" />
        <path d="M46 49 l9 2" stroke="#2D3142" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M65 51 l9 -2" stroke="#2D3142" strokeWidth="2.4" strokeLinecap="round" />
      </>
    ) : (
      <>
        <circle cx="51" cy="54" r="4" fill="#2D3142" />
        <circle cx="69" cy="54" r="4" fill="#2D3142" />
        <circle cx="52.4" cy="52.6" r="1.3" fill="#ffffff" />
        <circle cx="70.4" cy="52.6" r="1.3" fill="#ffffff" />
      </>
    );

  // Mun: 'celebrate' = öppet stort leende, 'happy' = leende, 'thinking' = liten
  // o-mun, 'idle' = mild kurva.
  const mouth =
    mood === 'celebrate' ? (
      <path d="M50 66 q10 14 20 0 q-10 6 -20 0 z" fill="#D95F5F" />
    ) : mood === 'happy' ? (
      <path
        d="M51 66 q9 9 18 0"
        fill="none"
        stroke="#2D3142"
        strokeWidth="3"
        strokeLinecap="round"
      />
    ) : mood === 'thinking' ? (
      <circle cx="60" cy="68" r="3.4" fill="none" stroke="#2D3142" strokeWidth="2.6" />
    ) : (
      <path
        d="M53 67 q7 5 14 0"
        fill="none"
        stroke="#2D3142"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    );

  // Glada rosa kinder vid positiva tillstånd.
  const cheeks =
    mood === 'happy' || mood === 'celebrate' ? (
      <>
        <circle cx="43" cy="63" r="4" fill="#F6B6B6" opacity="0.7" />
        <circle cx="77" cy="63" r="4" fill="#F6B6B6" opacity="0.7" />
      </>
    ) : null;

  return (
    <>
      {eyes}
      {cheeks}
      {mouth}
    </>
  );
};

// Liten "...""-pratbubbla som visas inuti figuren vid thinking-läget.
const thinkingDots = (
  <g>
    <rect
      x="78"
      y="24"
      width="30"
      height="18"
      rx="9"
      fill="#ffffff"
      stroke="#E8E4DE"
      strokeWidth="1.5"
    />
    <circle cx="86" cy="33" r="2" fill="#6B7280" />
    <circle cx="93" cy="33" r="2" fill="#6B7280" />
    <circle cx="100" cy="33" r="2" fill="#6B7280" />
  </g>
);

// Stjärnor som poppar runt figuren vid celebrate-läget.
const celebrateStars = (
  <g fill="#D4A843">
    <path d="M20 30 l2.2 4.6 5 .6 -3.6 3.5 .9 5 -4.5 -2.4 -4.5 2.4 .9 -5 -3.6 -3.5 5 -.6 z" />
    <path d="M98 18 l1.7 3.5 3.8 .5 -2.8 2.7 .7 3.8 -3.4 -1.8 -3.4 1.8 .7 -3.8 -2.8 -2.7 3.8 -.5 z" />
    <path d="M104 70 l1.7 3.5 3.8 .5 -2.8 2.7 .7 3.8 -3.4 -1.8 -3.4 1.8 .7 -3.8 -2.8 -2.7 3.8 -.5 z" />
  </g>
);

/**
 * Per-karaktär SVG-kropp. Ansiktet (renderFace) läggs ovanpå huvudet så att
 * alla karaktärer delar mood-uttryck men har egen igenkännbar form/färg.
 */
const renderBody = (character: CharacterName, mood: CharacterMood) => {
  const face = renderFace(mood);

  switch (character) {
    case 'fox':
      // Räv – orange, spetsiga öron, vit nos.
      return (
        <>
          <path d="M34 40 L30 18 L48 34 Z" fill="#E8924A" />
          <path d="M86 40 L90 18 L72 34 Z" fill="#E8924A" />
          <path d="M37 21 L33 30 L42 32 Z" fill="#FDF0E6" />
          <path d="M83 21 L87 30 L78 32 Z" fill="#FDF0E6" />
          <circle cx="60" cy="58" r="30" fill="#E8924A" />
          <path d="M44 64 a16 14 0 0 0 32 0 q-16 12 -32 0 z" fill="#FBF6EF" />
          <circle cx="60" cy="74" r="3.4" fill="#2D3142" />
          {face}
        </>
      );
    case 'robot':
      // Robot – mjuk blå, rundad fyrkant, antenn.
      return (
        <>
          <rect x="58" y="20" width="4" height="12" rx="2" fill="#357AA8" />
          <circle cx="60" cy="18" r="4" fill="#5AAF78" />
          <rect x="32" y="34" width="56" height="50" rx="16" fill="#4A90C4" />
          <rect x="40" y="44" width="40" height="28" rx="10" fill="#EAF3F9" />
          <g transform="translate(0,-4)">{face}</g>
          <rect x="50" y="84" width="20" height="6" rx="3" fill="#357AA8" />
        </>
      );
    case 'bear':
      // Björn – varm brun, runda öron.
      return (
        <>
          <circle cx="40" cy="38" r="11" fill="#C78F46" />
          <circle cx="80" cy="38" r="11" fill="#C78F46" />
          <circle cx="40" cy="38" r="5.5" fill="#FDF0E6" />
          <circle cx="80" cy="38" r="5.5" fill="#FDF0E6" />
          <circle cx="60" cy="58" r="31" fill="#C78F46" />
          <ellipse cx="60" cy="68" rx="15" ry="12" fill="#FBF1E0" />
          <ellipse cx="60" cy="63" rx="4" ry="3" fill="#2D3142" />
          {face}
        </>
      );
    case 'star':
      // Stjärna – varm amber, klassisk femuddig form.
      return (
        <>
          <path
            d="M60 20 L70 48 L100 50 L76 68 L84 98 L60 80 L36 98 L44 68 L20 50 L50 48 Z"
            fill="#D4A843"
            stroke="#C89A2E"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <g transform="translate(0,2)">{face}</g>
        </>
      );
    case 'frog':
      // Groda – grön, ögon ovanpå huvudet.
      return (
        <>
          <circle cx="42" cy="34" r="13" fill="#5AAF78" />
          <circle cx="78" cy="34" r="13" fill="#5AAF78" />
          <circle cx="42" cy="33" r="6" fill="#ffffff" />
          <circle cx="78" cy="33" r="6" fill="#ffffff" />
          <circle cx="43" cy="34" r="3" fill="#2D3142" />
          <circle cx="79" cy="34" r="3" fill="#2D3142" />
          <circle cx="60" cy="64" r="30" fill="#5AAF78" />
          <ellipse cx="60" cy="74" rx="20" ry="12" fill="#EAF5EF" />
          {/* Grodan har egen bred mun – ignorera den delade munnen, behåll kinder via face. */}
          <path
            d="M44 70 q16 16 32 0"
            fill="none"
            stroke="#2D3142"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </>
      );
    default:
      return face;
  }
};

// Animationskeyframes per mood. Förankrade nedtill (transform-origin) så att
// figuren gungar/studsar naturligt. prefers-reduced-motion stänger av allt.
const moodAnimation = (mood: CharacterMood) => {
  const animations = {
    idle: {
      '@keyframes characterIdle': {
        '0%, 100%': { transform: 'translateY(0) scale(1)' },
        '50%': { transform: 'translateY(-1.5px) scale(1.012)' },
      },
      animation: 'characterIdle 4s ease-in-out infinite',
    },
    happy: {
      '@keyframes characterHappy': {
        '0%, 100%': { transform: 'rotate(0deg)' },
        '25%': { transform: 'rotate(-3deg)' },
        '75%': { transform: 'rotate(3deg)' },
      },
      animation: 'characterHappy 1.6s ease-in-out infinite',
    },
    thinking: {
      '@keyframes characterThinking': {
        '0%, 100%': { transform: 'rotate(0deg)' },
        '50%': { transform: 'rotate(-5deg)' },
      },
      animation: 'characterThinking 2.4s ease-in-out infinite',
    },
    celebrate: {
      '@keyframes characterCelebrate': {
        '0%, 100%': { transform: 'translateY(0)' },
        '30%': { transform: 'translateY(-10px)' },
        '60%': { transform: 'translateY(-3px)' },
      },
      animation: 'characterCelebrate 0.9s ease-in-out infinite',
    },
  } as const;

  return {
    transformOrigin: 'center bottom',
    ...animations[mood],
    // Tillgänglighet: respektera systemets reduktion av rörelse.
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  };
};

const Character = ({ character, mood = 'idle', size = 96, decorative = false }: CharacterProps) => {
  // Unika animationsnamn behövs ej (keyframes är scoped per mood-namn och
  // delas felfritt), men ett unikt id används för SVG-titeln/a11y-koppling.
  const titleId = useId();
  const def = characters.find((c) => c.name === character) ?? characters[0];
  const label = `${def.label} (${moodLabels[mood]})`;

  const a11yProps = decorative
    ? { 'aria-hidden': true as const }
    : { role: 'img' as const, 'aria-labelledby': titleId };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        width: size,
        height: size,
        lineHeight: 0,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 120 120"
        {...a11yProps}
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'visible',
          ...moodAnimation(mood),
        }}
      >
        {!decorative && <title id={titleId}>{label}</title>}
        {groundShadow}
        {renderBody(character, mood)}
        {mood === 'thinking' && thinkingDots}
        {mood === 'celebrate' && celebrateStars}
      </Box>
    </Box>
  );
};

export default Character;
